import React, { useState, useEffect, useRef } from 'react';
import { ConfigProvider, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { DARK_THEME } from '../../dashboard/theme';
import { googleConfig } from '../../config';
import './index.css';
import '../auth.css';

// The frame shared by login, signup and password recovery: the dashboard's
// dark antd theme (so error, focus and disabled states come out right without
// per-state CSS overrides), the wordmark, a heading and the form card.
// "Giriş yap · Piyanos Link": the tab names the page, in its language
export const usePageTitle = (title) => {
    useEffect(() => {
        if (title) document.title = `${title} · Piyanos Link`;
    }, [title]);
};

const AuthLayout = ({ title, subtitle, children, footer }) => (
  <ConfigProvider theme={DARK_THEME}>
    <div className="green-container login-par-cont auth-page">
      <main className="login-container" id="components-form-normal-login">
        <a className="auth-wordmark" href="/">
          <span className="auth-wordmark__keys" aria-hidden="true">
            <i /><i className="is-black" /><i /><i className="is-black" /><i />
          </span>
          Piyanos Link
        </a>
        <h1 className="auth-title">{title}</h1>
        {subtitle && <p className="auth-sub">{subtitle}</p>}
        {children}
        {footer && <p className="auth-foot">{footer}</p>}
      </main>
    </div>
  </ConfigProvider>
);

// A server answer shown inside the form, next to the fields it is about,
// rather than as a toast at the top of the screen that is gone in seconds
export const FormAlert = ({ type = 'error', message }) =>
  message ? <Alert className="auth-alert" type={type} showIcon message={message} role="alert" /> : null;

// Google draws its button in an iframe at a fixed pixel width (200-400), so
// measure the column and ask for exactly that: the button then lines up
// with the full-width primary button above it.
export const GoogleButton = ({ text, onCredential, onError }) => {
    const [, i18n] = useTranslation();
    const wrap = useRef(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const measure = () => wrap.current && setWidth(Math.round(Math.min(400, Math.max(200, wrap.current.clientWidth))));
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    return (
        <div className="google-login-wrap" ref={wrap}>
            {width > 0 &&
                <GoogleOAuthProvider clientId={googleConfig.clientId}>
                    {/* Remounted on resize: the iframe only reads width once */}
                    <GoogleLogin
                        key={width}
                        text={text}
                        shape="pill"
                        size="large"
                        width={width}
                        locale={i18n.language.slice(0, 2)}
                        onSuccess={onCredential}
                        onError={onError}
                    />
                </GoogleOAuthProvider>}
        </div>
    );
};

// The error code a failed auth request carries, if any; network failures
// have no response at all
export const responseCode = err => err && err.response && err.response.data && err.response.data.code;

export default AuthLayout;
